import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Phone,
  Bike,
  ShieldCheck,
  Route,
  Sparkles,
  Zap,
  Clock,
  Award,
} from "lucide-react";

const FORMULES = [
  {
    name: "Moto A2 — 20 leçons",
    price: "700 €",
    tag: "A2",
    features: [
      "Hors circulation + circulation",
      "Livret de formation inclus",
      "Piste équipée sécurisée",
      "Moniteur BEPECASER moto",
    ],
    testId: "moto-a2",
  },
  {
    name: "30h moto en accéléré",
    price: "1 400 €",
    highlight: true,
    tag: "Accéléré",
    features: [
      "30 leçons intensives",
      "Programme concentré",
      "Passage rapide à l'examen",
      "Encadrement renforcé",
    ],
    testId: "moto-accelere",
  },
  {
    name: "Maxi Scooter A2",
    price: "700 €",
    tag: "Scooter",
    features: [
      "Formation complète",
      "Livret + suivi personnalisé",
      "Maniabilité + circulation",
    ],
    testId: "moto-maxi-scooter",
  },
  {
    name: "Moto 125cm³",
    price: "300 €",
    tag: "Passerelle B",
    features: [
      "Formation 7h obligatoire",
      "Pour titulaires du permis B",
      "Attestation de suivi remise",
    ],
    testId: "moto-125",
  },
];

const AVANTAGES = [
  {
    icon: Bike,
    title: "Flotte moto récente",
    desc: "Nos motos-écoles sont entretenues et adaptées à chaque profil : débutants comme confirmés.",
  },
  {
    icon: ShieldCheck,
    title: "Sécurité avant tout",
    desc: "Équipement fourni, piste privée pour le plateau, et formation aux réflexes indispensables.",
  },
  {
    icon: Route,
    title: "Circulation Île-de-France",
    desc: "Nos moniteurs vous préparent aux réalités du trafic parisien et de la banlieue Ouest.",
  },
];

const ETAPES = [
  {
    step: "01",
    title: "Évaluation de départ",
    desc: "Un moniteur mesure votre niveau (novice ou déjà cycliste) pour bâtir un plan personnalisé.",
  },
  {
    step: "02",
    title: "Plateau — hors circulation",
    desc: "Maniabilité lente et rapide sur notre piste. Maîtrise de la machine et exercices d'examen.",
  },
  {
    step: "03",
    title: "Circulation",
    desc: "Trajets progressifs : quartier, boulevards, périphérique. Analyse à chaud après chaque séance.",
  },
  {
    step: "04",
    title: "Examens",
    desc: "Présentation au plateau puis à la circulation. Nous restons à vos côtés jusqu'à la réussite.",
  },
];

const CATEGORIES = [
  {
    code: "AM",
    age: "Dès 14 ans",
    label: "Cyclomoteur / Scooter 50 cm³",
    detail: "BSR — formation 8h. Idéal pour circuler en autonomie à Boulogne-Billancourt.",
  },
  {
    code: "A1",
    age: "Dès 16 ans",
    label: "Moto 125 cm³ (11 kW)",
    detail: "Ouvre l'accès aux 125 sans passer par le permis B.",
  },
  {
    code: "A2",
    age: "Dès 18 ans",
    label: "Moto bridée 35 kW (47,5 ch)",
    detail: "Formation complète plateau + circulation. Base obligatoire avant l'accès au A.",
  },
  {
    code: "A",
    age: "Dès 20 ans",
    label: "Moto sans limite de puissance",
    detail: "Passerelle A2 → A : 7h de formation après 2 ans de permis A2.",
  },
];

export default function Moto() {
  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body">
      <Seo
        title="Permis Moto Boulogne-Billancourt · A2, 125 cm³, accéléré | DDSBillancourt"
        description="Permis moto à Boulogne-Billancourt (92) : permis A2 20 leçons 700 €, formation 125 cm³ 300 €, 30 heures moto en accéléré 1 400 €. Moniteurs diplômés, plateau + circulation, conduite 7j/7."
        path="/moto"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://ddsbillancourt.fr/" },
            { "@type": "ListItem", position: 2, name: "Permis Moto", item: "https://ddsbillancourt.fr/moto" },
          ],
        }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1a2332] text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(255,140,66,0.35), transparent 40%), radial-gradient(circle at 80% 60%, rgba(0,168,107,0.25), transparent 45%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-8 py-14 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#FF8C42] text-sm font-headings font-semibold mb-8"
            data-testid="moto-back"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
          </Link>

          <div className="flex items-center gap-2 text-[#FF8C42] font-headings font-bold text-sm uppercase tracking-[0.2em]">
            <Bike className="w-4 h-4" /> Permis Moto & Scooter
          </div>
          <h1
            className="mt-4 font-headings font-black text-4xl md:text-6xl tracking-tight max-w-3xl"
            data-testid="moto-title"
          >
            Passez votre permis moto à Boulogne-Billancourt.
          </h1>
          <p className="mt-5 text-white/80 max-w-2xl text-lg">
            De l&apos;AM au A2, du 125&nbsp;cm³ au maxi-scooter : nos formations moto vous
            emmènent du plateau à la circulation, avec un moniteur dédié et une
            piste équipée à quelques minutes de Paris.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#formules-moto"
              data-testid="moto-cta-formules"
              className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Voir les formules <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="tel:+33141315061"
              data-testid="moto-cta-call"
              className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-[#00a86b] hover:text-[#00a86b] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> 01 41 31 50 61
            </a>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl">
            {AVANTAGES.map((a) => (
              <div
                key={a.title}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10"
              >
                <div className="w-10 h-10 rounded-xl bg-[#FF8C42]/20 flex items-center justify-center">
                  <a.icon className="w-5 h-5 text-[#FF8C42]" />
                </div>
                <div className="mt-3 font-headings font-bold text-white">
                  {a.title}
                </div>
                <p className="mt-1 text-sm text-white/70">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formules */}
      <section
        id="formules-moto"
        className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-24"
      >
        <div className="max-w-2xl">
          <div className="text-[#00a86b] font-headings font-bold text-sm uppercase tracking-[0.2em]">
            Nos formules
          </div>
          <h2 className="mt-3 font-headings font-black text-3xl md:text-4xl tracking-tight">
            Choisissez votre formation moto.
          </h2>
          <p className="mt-3 text-[#5b6577]">
            Tarifs clairs, sans frais cachés. Paiement en plusieurs fois disponible.
          </p>
        </div>

        <div
          className="mt-10 grid md:grid-cols-2 gap-5"
          data-testid="moto-formules"
        >
          {FORMULES.map((item) => (
            <div
              key={item.name}
              data-testid={`moto-item-${item.testId}`}
              className={`relative bg-white rounded-2xl p-6 md:p-8 border-2 flex flex-col ${
                item.highlight
                  ? "border-[#FF8C42] shadow-xl"
                  : "border-black/5 shadow-sm hover:border-[#00a86b]/40 hover:shadow-lg"
              }`}
            >
              {item.tag && (
                <div
                  className={`absolute -top-3 left-6 font-headings font-bold text-xs tracking-widest uppercase px-3 py-1 rounded-full shadow-md ${
                    item.highlight
                      ? "bg-[#FF8C42] text-white"
                      : "bg-[#1a2332] text-white"
                  }`}
                >
                  {item.tag}
                </div>
              )}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <h3 className="font-headings font-bold text-lg md:text-xl leading-snug">
                    {item.name}
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {item.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-center gap-2 text-sm text-[#5b6577]"
                      >
                        <Check className="w-4 h-4 text-[#00a86b] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-right">
                  <div
                    className={`font-headings font-black text-3xl md:text-4xl tracking-tight ${
                      item.highlight ? "text-[#FF8C42]" : "text-[#00a86b]"
                    }`}
                  >
                    {item.price}
                  </div>
                  <div className="text-xs text-[#5b6577] mt-1">TTC</div>
                </div>
              </div>
              <Link
                to="/reservation"
                data-testid={`moto-cta-${item.testId}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 font-headings font-bold rounded-xl px-5 py-3 text-sm transition-colors ${
                  item.highlight
                    ? "bg-[#FF8C42] hover:bg-[#e8763a] text-white shadow-md"
                    : "border-2 border-[#1a2332]/15 text-[#1a2332] hover:border-[#00a86b] hover:text-[#00a86b]"
                }`}
              >
                S&apos;inscrire <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center text-sm text-[#5b6577] italic">
          1 leçon = 55 minutes · Équipement pédagogique fourni (casque, gants d&apos;essai, gilet)
        </div>
      </section>

      {/* Catégories */}
      <section className="bg-white py-16 md:py-24 border-y border-black/5">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="max-w-2xl">
            <div className="text-[#FF8C42] font-headings font-bold text-sm uppercase tracking-[0.2em]">
              Les catégories du permis moto
            </div>
            <h2 className="mt-3 font-headings font-black text-3xl md:text-4xl tracking-tight">
              Quel permis correspond à votre projet&nbsp;?
            </h2>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="moto-categories">
            {CATEGORIES.map((c) => (
              <div
                key={c.code}
                className="rounded-2xl p-6 border-2 border-black/5 bg-[#f8faff] hover:border-[#00a86b]/50 hover:bg-white transition-all"
                data-testid={`moto-category-${c.code.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#1a2332] text-white font-headings font-black text-lg flex items-center justify-center">
                    {c.code}
                  </div>
                  <div className="text-xs font-headings font-bold text-[#00a86b] uppercase tracking-widest">
                    {c.age}
                  </div>
                </div>
                <div className="mt-4 font-headings font-bold text-[#1a2332]">
                  {c.label}
                </div>
                <p className="mt-2 text-sm text-[#5b6577]">{c.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Étapes */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <div className="text-[#00a86b] font-headings font-bold text-sm uppercase tracking-[0.2em]">
            Notre méthode
          </div>
          <h2 className="mt-3 font-headings font-black text-3xl md:text-4xl tracking-tight">
            Du plateau au permis en poche.
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="moto-etapes">
          {ETAPES.map((e) => (
            <div
              key={e.step}
              className="relative bg-white rounded-2xl p-6 border border-black/5 shadow-sm"
            >
              <div className="font-headings font-black text-4xl text-[#FF8C42]/25">
                {e.step}
              </div>
              <div className="mt-2 font-headings font-bold text-[#1a2332]">
                {e.title}
              </div>
              <p className="mt-2 text-sm text-[#5b6577]">{e.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bandeau atouts */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 pb-16">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-[#00a86b] text-white rounded-2xl p-6 flex items-start gap-4" data-testid="moto-atout-financement">
            <Sparkles className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <div className="font-headings font-bold">Paiement en plusieurs fois</div>
              <p className="text-sm text-white/85 mt-1">Étalez votre formation moto sans surprise.</p>
            </div>
          </div>
          <div className="bg-[#1a2332] text-white rounded-2xl p-6 flex items-start gap-4" data-testid="moto-atout-piste">
            <Clock className="w-6 h-6 text-[#FF8C42] shrink-0 mt-0.5" />
            <div>
              <div className="font-headings font-bold">Créneaux flexibles</div>
              <p className="text-sm text-white/70 mt-1">Semaine, samedi ou intensif : on s&apos;adapte à votre planning.</p>
            </div>
          </div>
          <div className="bg-[#FF8C42] text-white rounded-2xl p-6 flex items-start gap-4" data-testid="moto-atout-reussite">
            <Award className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <div className="font-headings font-bold">Réussite au 1er passage</div>
              <p className="text-sm text-white/90 mt-1">Un moniteur BEPECASER dédié, une piste équipée, un suivi rapproché.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 pb-20">
        <div className="bg-[#1a2332] text-white rounded-2xl px-6 py-10 md:px-10 md:py-14 text-center" data-testid="moto-contact-band">
          <div className="inline-flex items-center gap-2 text-[#FF8C42] font-headings font-bold text-xs tracking-[0.2em] uppercase">
            <Zap className="w-4 h-4" /> Prêt à démarrer&nbsp;?
          </div>
          <div className="mt-3 font-headings font-black text-2xl md:text-3xl">
            Votre première leçon moto vous attend.
          </div>
          <p className="mt-2 text-white/70 max-w-xl mx-auto">
            Réservez en ligne ou appelez-nous pour un devis personnalisé. Nous étudions
            votre projet et vos délais ensemble.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link
              to="/reservation"
              className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm shadow-md transition-colors"
              data-testid="moto-reservation"
            >
              Réserver ma leçon <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:+33141315061"
              className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#00a86b] hover:text-[#00a86b] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors"
              data-testid="moto-call"
            >
              <Phone className="w-4 h-4" /> 01 41 31 50 61
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
